import { useEffect, useState } from 'react';
import { collection, doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase/config';
import type { DrillDoc, OrgConfig, ScenarioDoc, UserDoc } from './types';

/** Anything the ladder can put behind a rung. */
export type StageContent = (Partial<DrillDoc> & Partial<ScenarioDoc>) & { stage: string; id: string };

/**
 * Everything the rep's home screen needs, live. Three subscriptions: the org
 * config, the rep's own user document, and the drill content for their org.
 * Practice mode makes zero function calls (§2) — this is all direct Firestore.
 */
export function useOrgData(orgId: string | null, uid: string | null) {
  const [config, setConfig] = useState<OrgConfig | null>(null);
  const [user, setUser] = useState<UserDoc | null>(null);
  /**
   * Whether the server has told us this person has no profile in this company,
   * as opposed to not having answered yet. Both look like `user === null`, and
   * they need completely different screens: one is a spinner, the other is an
   * account that was never provisioned and cannot do anything until it is.
   */
  const [userMissing, setUserMissing] = useState(false);
  const [content, setContent] = useState<StageContent[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!orgId || !uid) return;
    setLoading(true);

    const fail = (what: string) => (err: unknown) => {
      console.error(`${what} subscription failed`, err);
      setError(`Could not load ${what}.`);
      setLoading(false);
    };

    const unsubConfig = onSnapshot(
      doc(db, `orgs/${orgId}/config/current`),
      (snap) => { setConfig(snap.data() as OrgConfig ?? null); setLoading(false); },
      fail('your organization settings')
    );

    const unsubUser = onSnapshot(
      doc(db, `orgs/${orgId}/users/${uid}`),
      (snap) => { setUser((snap.data() as UserDoc) ?? null); setUserMissing(!snap.exists()); },
      fail('your progress')
    );

    // Drills and simulations share the `scenarios` collection (§12.1), and the
    // ladder puts whichever one serves a stage behind that rung.
    const unsubContent = onSnapshot(
      collection(db, `orgs/${orgId}/scenarios`),
      (snap) => setContent(snap.docs.map((d) => d.data() as StageContent)),
      fail('your exercises')
    );

    return () => { unsubConfig(); unsubUser(); unsubContent(); };
  }, [orgId, uid]);

  return { config, user, userMissing, content, loading, error };
}
